// Parses the Town of Paradise's weekly "Facility Cleaning Schedule" Excel
// document into the same { windows, min_staff, restrictions, notes } shape
// the Town Portal's manual entry form already uses for rental_schedules —
// so an imported file can be reviewed/edited in the exact same UI before
// being submitted, no separate save path needed.
//
// The document's own instructions say the template is stable week to week
// ("The day's of the week are not to change... only the dates and times are
// adjusted") — one block of text per building, each followed by a
// Day/Date/[Rental Time(s)]/Cleaning Time/[Notes] table. This parser is
// tuned to that fixed layout and the specific phrasing patterns TC/the Town
// actually uses in it (verified against the Aug 17-23, 2026 copy).
//
// It's deliberately conservative: anything it can't confidently parse gets
// flagged in `warnings` and falls back to a plain "anytime" slot rather than
// being silently dropped, so a reviewer always sees when something needs a
// manual look.
import * as XLSX from 'xlsx';

export const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

// Ordered, most-specific-first — text is tested top to bottom and the first
// match wins (e.g. "Outdoor Washroom ... Soccer" must be checked before the
// plain "Soccer Complex" pattern, or every mention of the complex would
// match the wrong building).
const BUILDING_MATCHERS = [
  { id: 'M', test: (t) => /35 glenco|1270 kenmount/i.test(t) },
  { id: 'N', test: (t) => /1655 topsail/i.test(t) },
  { id: 'D', test: (t) => /wastewater treatment/i.test(t) },
  { id: 'F', test: (t) => /rpycc/i.test(t) },
  { id: 'Q', test: (t) => /outdoor washroom/i.test(t) && /dianne whalen|soccer/i.test(t) },
  { id: 'H', test: (t) => /dianne whalen|soccer complex/i.test(t) },
  { id: 'G', test: (t) => /paradise park change rooms/i.test(t) },
  { id: 'O', test: (t) => /paradise park/i.test(t) },
  { id: 'E', test: (t) => /paradise double ice complex|\bpdic\b/i.test(t) },
  { id: 'J', test: (t) => /milton clubhouse/i.test(t) },
  { id: 'P', test: (t) => /peter barry duff washrooms/i.test(t) },
  { id: 'K', test: (t) => /peter barry duff/i.test(t) },
  { id: 'I', test: (t) => /st\.?\s*thomas line community/i.test(t) },
  { id: 'L', test: (t) => /st\.?\s*thomas line pump/i.test(t) },
];

function matchBuilding(text) {
  for (const b of BUILDING_MATCHERS) if (b.test(text)) return b.id;
  return null;
}

function normalizeTime(raw) {
  const s = raw.trim().toLowerCase().replace(/\s+/g, '');
  const m = /^(\d{1,2})(?::(\d{2}))?(am|pm)$/.exec(s);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  if (m[3] === 'pm' && h !== 12) h += 12;
  if (m[3] === 'am' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function findAllTimes(text) {
  const re = /(\d{1,2}(?::\d{2})?\s*(?:am|pm))/gi;
  const out = [];
  let m;
  while ((m = re.exec(text))) {
    const t = normalizeTime(m[1]);
    if (t) out.push(t);
  }
  return out;
}

// Turns one "Cleaning Time" cell into 0+ window objects. Additive, not
// first-match-wins — a cell like "1 Cleaning anytime,1 after 11pm" needs
// BOTH an anytime slot and a timed slot, so each pattern type is checked
// independently rather than stopping at the first one found.
export function parseCleaningText(rawText) {
  const text = (rawText || '').trim();
  if (!text) return { windows: [{ type: 'anytime' }], unparsed: false };

  // "Closed" (e.g. a holiday) is a real, meaningful answer — no cleaning
  // that day at all — not an unrecognized phrase to flag for review.
  if (/\bclosed\b/i.test(text)) return { windows: [], unparsed: false };

  const windows = [];

  // "10:00pm - 6:00am", "1pm-2pm & 9pm-10pm"
  const rangeRe = /(\d{1,2}(?::\d{2})?\s*(?:am|pm))\s*[-–]\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm))/gi;
  let rm;
  while ((rm = rangeRe.exec(text))) {
    const start = normalizeTime(rm[1]);
    const end = normalizeTime(rm[2]);
    if (start && end) windows.push({ type: 'time', start, end, note: text });
  }

  // "after 9:30pm", "after 1:45pm & 7:30pm", "after 4:00pm and 8:30pm"
  const afterRe = /after\s+([^.;]*)/gi;
  let am;
  while ((am = afterRe.exec(text))) {
    for (const t of findAllTimes(am[1])) windows.push({ type: 'time', start: t, note: text });
  }

  // "once at 11pm", "once at midday and 11pm"
  const atRe = /\bat\s+([^)]*)/gi;
  let tm;
  while ((tm = atRe.exec(text))) {
    const tail = tm[1].replace(/midday|noon/gi, '12:00pm');
    for (const t of findAllTimes(tail)) windows.push({ type: 'time', start: t, note: text });
  }

  // "2 Cleanings anytime", "1 Cleaning anytime"
  const anytimeRe = /(\d+)?\s*cleanings?\s+anytime/gi;
  let cm;
  while ((cm = anytimeRe.exec(text))) {
    const n = cm[1] ? parseInt(cm[1], 10) : 1;
    for (let i = 0; i < n; i++) windows.push({ type: 'anytime' });
  }
  if (windows.length === 0 && /anytime/i.test(text)) windows.push({ type: 'anytime' });

  if (windows.length === 0) return { windows: [{ type: 'anytime', note: text }], unparsed: true };
  return { windows, unparsed: false };
}

function parseRows(rows) {
  const warnings = [];
  const sections = [];

  let year = new Date().getFullYear();
  for (let i = 0; i < Math.min(rows.length, 6); i++) {
    const ym = /\b(20\d{2})\b/.exec((rows[i] || []).join(' '));
    if (ym) { year = parseInt(ym[1], 10); break; }
  }

  let weekStart = null;
  let pendingBuildingId = null;
  let pendingTitleLines = [];

  function flushPending() {
    if (!pendingBuildingId && pendingTitleLines.length === 0) return;
    if (pendingBuildingId) {
      // No day-table ever showed up for this one — it's a standing rule
      // stated in prose instead of a table (e.g. Paradise Park's outside
      // washrooms: "once a day 7 days a week, anytime after 9pm"). Parse
      // it once and apply the same slot(s) to every day.
      const desc = pendingTitleLines.join(' ');
      const { windows, unparsed } = parseCleaningText(desc);
      if (unparsed) warnings.push(`[${pendingBuildingId}] standing note not fully understood — please check: "${desc.slice(0, 100)}"`);
      const days = {};
      WEEK_DAYS.forEach((d) => { days[d] = windows.map((w) => ({ ...w })); });
      sections.push({ buildingId: pendingBuildingId, rawTitle: desc, days, fromProseOnly: true });
    } else {
      warnings.push(`Found a description that didn't match any known building: "${pendingTitleLines.join(' ').slice(0, 100)}"`);
    }
    pendingBuildingId = null;
    pendingTitleLines = [];
  }

  let i = 0;
  while (i < rows.length) {
    const row = rows[i] || [];
    const c0 = (row[0] || '').toString().trim();
    const c1 = (row[1] || '').toString().trim();
    const isHeaderRow = c0.toLowerCase() === 'day' && c1.toLowerCase() === 'date';

    if (isHeaderRow) {
      if (!pendingBuildingId) {
        const blob = pendingTitleLines.join(' \n ');
        pendingBuildingId = matchBuilding(blob);
        if (!pendingBuildingId) warnings.push(`Could not match a building for section: "${blob.slice(0, 100)}"`);
      }
      const buildingId = pendingBuildingId;
      const rawTitle = pendingTitleLines.join(' \n ');
      const header = row.map((c) => (c || '').toString().trim());
      const cleaningIdx = header.findIndex((h) => /cleaning time|cleaning instructions/i.test(h));
      const notesIdx = header.findIndex((h) => /^notes$/i.test(h));

      const section = { buildingId, rawTitle, days: {} };
      i++;
      let lastDay = null;
      let interrupted = false;
      while (i < rows.length) {
        const r = rows[i] || [];
        const allBlank = r.every((c) => (c || '').toString().trim() === '');
        if (allBlank) { i++; break; }

        const dayCell = (r[0] || '').toString().trim();
        const dayKey = dayCell.toLowerCase();
        const restNonBlank = r.some((c, idx) => idx !== 0 && (c || '').toString().trim() !== '');

        if (WEEK_DAYS.includes(dayKey)) {
          const cleaningText = cleaningIdx >= 0 ? (r[cleaningIdx] || '').toString().trim() : '';
          const notesText = notesIdx >= 0 ? (r[notesIdx] || '').toString().trim() : '';

          if (dayKey === 'monday' && !weekStart) {
            const dm = /(\d{1,2})-([A-Za-z]{3})/.exec((r[1] || '').toString());
            if (dm) {
              const mon = MONTHS[dm[2].toLowerCase()];
              if (mon) weekStart = `${year}-${String(mon).padStart(2, '0')}-${String(parseInt(dm[1], 10)).padStart(2, '0')}`;
            }
          }
          const { windows, unparsed } = parseCleaningText(cleaningText);
          if (unparsed) warnings.push(`[${buildingId || '???'}] ${dayCell}: couldn't understand "${cleaningText}" — check this slot`);
          if (notesText) windows.forEach((w) => { w.note = w.note ? `${w.note}; ${notesText}` : notesText; });
          section.days[dayKey] = (section.days[dayKey] || []).concat(windows);
          lastDay = dayKey;
          i++;
        } else if (dayCell && restNonBlank && lastDay) {
          // Continuation row, e.g. "(As per cleaning checklist)" carrying
          // extra info (min-staff instructions) for the previous day row.
          const cleaningText = cleaningIdx >= 0 ? (r[cleaningIdx] || '').toString().trim() : '';
          const notesText = notesIdx >= 0 ? (r[notesIdx] || '').toString().trim() : '';
          const extra = [cleaningText, notesText].filter(Boolean).join('; ');
          if (extra) (section.days[lastDay] || []).forEach((w) => { w.note = w.note ? `${w.note}; ${extra}` : extra; });
          i++;
        } else if (dayCell && !restNonBlank && matchBuilding(dayCell)) {
          // Next building's title line ran straight into this table with no
          // blank-row separator (observed between adjacent entries) — stop
          // here and let the outer loop pick this row up as a new title.
          interrupted = true;
          break;
        } else if (dayCell && !restNonBlank) {
          // A continuation row with nothing left to add (e.g. Monday closed,
          // so there's no min-staff note for it) — not a new building title,
          // just an empty line. Previously this was indistinguishable from
          // the case above and silently truncated the rest of that
          // building's week (confirmed: PDIC and PRYCC's Tuesday–Sunday rows
          // were being dropped whenever Monday's continuation line had no
          // note text). Only treat it as a new title if it actually matches
          // one.
          i++;
        } else {
          i++;
        }
      }
      sections.push(section);
      pendingBuildingId = null;
      pendingTitleLines = [];
      if (interrupted) continue;
    } else {
      if (c0) {
        const singleMatch = matchBuilding(c0);
        if (singleMatch && pendingBuildingId && singleMatch !== pendingBuildingId) {
          flushPending();
          pendingBuildingId = singleMatch;
          pendingTitleLines = [c0];
        } else if (singleMatch && !pendingBuildingId) {
          pendingBuildingId = singleMatch;
          pendingTitleLines = [c0];
        } else {
          pendingTitleLines.push(c0);
        }
      }
      i++;
    }
  }
  flushPending();

  return { weekStart, sections, warnings };
}

// Converts the internal day-grouped sections into the exact
// { selectedBuildings, buildingData } shape TownPortalApp's form/review/
// submit code already works with, so importing a file is just "pre-fill the
// same state the manual form would produce" — no separate save path.
function toPortalState(sections) {
  const selectedBuildings = new Set();
  const buildingData = {};
  const unmatched = [];

  sections.forEach((section, idx) => {
    if (!section.buildingId) {
      unmatched.push({ key: `unmatched-${idx}`, rawTitle: section.rawTitle, days: section.days });
      return;
    }
    const windows = [];
    WEEK_DAYS.forEach((day) => {
      (section.days[day] || []).forEach((w) => windows.push({ day, ...w }));
    });
    selectedBuildings.add(section.buildingId);
    buildingData[section.buildingId] = {
      windows,
      min_staff: 1,
      restrictions: '',
      notes: section.fromProseOnly ? section.rawTitle : '',
    };
  });

  return { selectedBuildings, buildingData, unmatched };
}

// Main entry point: give it the uploaded File (or ArrayBuffer), get back
// portal-ready state plus a weekStart guess and any warnings to show the
// user before they submit.
export async function parseWeeklyScheduleFile(file) {
  const buf = file instanceof ArrayBuffer ? file : await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buf), { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, defval: '' });
  const { weekStart, sections, warnings } = parseRows(rows);
  const { selectedBuildings, buildingData, unmatched } = toPortalState(sections);
  return { weekStart, selectedBuildings, buildingData, unmatched, warnings, sectionCount: sections.length };
}
